import {
  expectNonEmptyString,
  expectNumber,
  expectRecord,
  type JsonValue,
  ProtocolError,
  toJson,
} from "./json.js";
import { isPortRef, type PortRef, type Wire } from "./messages.js";

/**
 * What is spoken over a capability's `MessagePort` (§5.3).
 *
 * One request/response protocol for all four kinds; the kinds differ only in
 * which methods they answer to (`CAP_METHODS`). A result may carry a port,
 * which is how a `collection` hands out an `object` and how `exec` hands out a
 * `stream`: authority is delegated by passing a port, never by naming a thing
 * and hoping the far side checks.
 */

export type CapCallId = number;

/** holder → capability */
export type CapCall = {
  t: "call";
  id: CapCallId;
  method: string;
  params?: JsonValue;
};

/** capability → holder, in reply to a call */
export type CapResult =
  | { t: "result"; id: CapCallId; value?: JsonValue; port?: MessagePort }
  | { t: "error"; id: CapCallId; message: string };

/** capability → holder, unsolicited (what `subscribe` turns on) */
export type CapEvent = { t: "event"; event: string; payload?: JsonValue };

export type CapMessage = CapCall | CapResult | CapEvent;

export const CAP_MESSAGE_TAGS: ReadonlySet<string> = new Set<string>([
  "call",
  "result",
  "error",
  "event",
]);

export function encodeCapMessage(message: CapMessage): Wire {
  switch (message.t) {
    case "call": {
      const body: Record<string, unknown> = {
        t: "call",
        id: expectNumber(message.id, "id"),
        method: expectNonEmptyString(message.method, "method"),
      };
      if (message.params !== undefined) body["params"] = toJson(message.params, "params");
      return { body, transfer: [] };
    }
    case "result": {
      const body: Record<string, unknown> = { t: "result", id: expectNumber(message.id, "id") };
      if (message.value !== undefined) body["value"] = toJson(message.value, "value");
      if (message.port === undefined) return { body, transfer: [] };
      body["port"] = { $port: 0 } satisfies PortRef;
      return { body, transfer: [message.port] };
    }
    case "error":
      return {
        body: {
          t: "error",
          id: expectNumber(message.id, "id"),
          message: String(message.message),
        },
        transfer: [],
      };
    case "event": {
      const body: Record<string, unknown> = {
        t: "event",
        event: expectNonEmptyString(message.event, "event"),
      };
      if (message.payload !== undefined) body["payload"] = toJson(message.payload, "payload");
      return { body, transfer: [] };
    }
    default:
      throw new ProtocolError(
        `unknown capability message ${JSON.stringify((message as { t: unknown }).t)}`,
        "",
      );
  }
}

export function decodeCapMessage(body: unknown, ports: readonly MessagePort[]): CapMessage {
  const record = expectRecord(body, "");
  const tag = record["t"];
  if (typeof tag !== "string" || !CAP_MESSAGE_TAGS.has(tag)) {
    throw new ProtocolError(`unknown capability message ${JSON.stringify(tag)}`, "t");
  }

  switch (tag) {
    case "call": {
      const call: CapCall = {
        t: "call",
        id: expectNumber(record["id"], "id"),
        method: expectNonEmptyString(record["method"], "method"),
      };
      if (record["params"] !== undefined) call.params = record["params"] as JsonValue;
      return call;
    }
    case "result": {
      const result: CapResult = { t: "result", id: expectNumber(record["id"], "id") };
      if (record["value"] !== undefined) result.value = record["value"] as JsonValue;
      const port = record["port"];
      if (port !== undefined) {
        if (!isPortRef(port)) throw new ProtocolError("expected a port reference", "port");
        const resolved = ports[port.$port];
        if (resolved === undefined) {
          throw new ProtocolError(`no transferred port at index ${port.$port}`, "port");
        }
        result.port = resolved;
      }
      return result;
    }
    case "error":
      return {
        t: "error",
        id: expectNumber(record["id"], "id"),
        message: String(record["message"] ?? "capability call failed"),
      };
    default: {
      const event: CapEvent = {
        t: "event",
        event: expectNonEmptyString(record["event"], "event"),
      };
      if (record["payload"] !== undefined) event.payload = record["payload"] as JsonValue;
      return event;
    }
  }
}

export function sendCapMessage(
  port: { postMessage(message: unknown, transfer: Transferable[]): void },
  message: CapMessage,
): void {
  const wire = encodeCapMessage(message);
  port.postMessage(wire.body, wire.transfer);
}

export function readCapMessage(event: MessageEvent): CapMessage {
  return decodeCapMessage(event.data, event.ports);
}

/* ----------------------------------------------------- documented payloads */

/** `object.get` */
export type ObjectContent = { content: string; type: string; name: string };
/** `object` subscription event */
export type ObjectChange = { content: string };
/** `collection.list` entry */
export type CollectionEntry = { id: string; type: string; name: string; created: number };
